import type { OperationItem } from '../types'
import { useQueryClient } from '@tanstack/vue-query'
import { useNow } from '@vueuse/core'
import { OPERATIONS_CACHE_KEYS } from '../constants'
import { isOperationUnfinished, isOperationWorking } from '../utils/lifecycle'
import { useRestaurantOperationsContext } from './provideRestaurantOperations'

const RECENT_LIMIT = 5

export type BOCItem = OperationItem

export function useBOC() {
  const { $orpc } = useNuxtApp()
  const queryClient = useQueryClient()
  const now = useNow({ interval: 10_000 })
  const { items, restaurantSlug } = useRestaurantOperationsContext()

  // One pass over the feed: the panel's two lists and the pill's spinner all
  // read the same partition. "In progress" holds everything the platform still
  // owes an outcome — progressing runs plus resumable ones — which is exactly
  // the set the row view refuses to make dismissable. `workingCount` is
  // narrower: only runs consuming compute animate the pill.
  const partition = computed(() => {
    const active: BOCItem[] = []
    const recent: BOCItem[] = []
    let workingCount = 0

    for (const item of items.value) {
      if (isOperationUnfinished(item, now.value)) {
        active.push(item)
        if (isOperationWorking(item, now.value)) {
          workingCount += 1
        }
        continue
      }

      if (recent.length < RECENT_LIMIT) {
        recent.push(item)
      }
    }

    return { active, recent, workingCount }
  })

  const active = computed(() => partition.value.active)
  const recent = computed(() => partition.value.recent)
  const workingCount = computed(() => partition.value.workingCount)

  async function dismiss(item: BOCItem) {
    await $orpc.operations.dismiss({
      operationId: item.id,
      restaurantSlug: item.restaurantSlug,
    })
    await queryClient.invalidateQueries({
      queryKey: OPERATIONS_CACHE_KEYS.envelopes(restaurantSlug),
    })
  }

  return { active, recent, workingCount, now, dismiss }
}
